<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import ExcelJS from 'exceljs'
import { del, get, post } from '@/api'
import { useConfirmDialog } from '@/composables/useConfirmDialog'
import { MAPPING_FILE_TYPES, getMappingFileType, getMappingFileTypeOptions } from '@/config/mappingFileConfig'
import { useClientStore } from '@/stores/client'

definePage({
  meta: {
    title: '매핑 입력',
    action: 'read',
    subject: 'DriverConfig',
  },
})

const { snackbar, handleError } = useErrorHandler()

// 스토어
const clientStore = useClientStore()
const selectedClient = computed(() => clientStore.selectedClient)

// 인터페이스
interface DriverConfig {
  id: number
  download_driver: {
    id: number
    name: string
  }
}

interface SheetInfo {
  id: number
  name: string
  stats: ValidationStats
  driverConfigId: number | null
  stored: boolean
}

interface MappingRow {
  client_item_code: string
  target_item_code: string
  config_id?: number
}

interface IncompleteRow {
  client_item_code: string | null
  target_item_code: string | null
}

interface ValidationStats {
  valid: number
  duplicate: number
  incomplete: number
  empty: number
}

interface MappingData {
  id: number
  name: string
  validData: MappingRow[]
  incompleteData: IncompleteRow[]
  stats: ValidationStats
}

// 상태
const file = ref<File | null>(null)
const mappingFileType = ref(MAPPING_FILE_TYPES[0].id)
const sheets = ref<SheetInfo[]>([])
const mappings = ref<MappingData[]>([])
const clientDriverConfigs = ref<DriverConfig[]>([])
const hasEmptyCode = ref(false)
const loading = ref(false)
const processingSheetId = ref<number | null>(null)
const showIncompleteSheets = ref<Set<number>>(new Set())

// Composables

interface ConfirmDialogData {
  configId: number
  sheetId: number
  mappingData: MappingRow[]
}

const confirmDialog = useConfirmDialog<ConfirmDialogData>()

// 미리보기 탭
const previewTab = ref(0)

// 파일 타입 옵션 (설정 파일에서 가져옴)
const fileTypeOptions = getMappingFileTypeOptions()

// 파일 변경 감시
watch(file, async newFile => {
  if (newFile)
    await parseFile(newFile)

  else
    resetMappings()
})

// ExcelJS Cell.value 객체(하이퍼링크/RichText/수식) 를 primitive 로 언래핑.
// SheetJS 는 셀을 primitive 로 반환하던 것을 ExcelJS 는 객체로 반환할 수 있으므로
// normalizeCode 의 String() 오염을 막기 위해 사전 추출 필요. #678
const unwrapCellValue = (value: unknown): unknown => {
  if (value == null) return ''
  if (typeof value !== 'object') return value

  const obj = value as Record<string, unknown>
  if (typeof obj.text === 'string') return obj.text
  if (Array.isArray(obj.richText))
    return (obj.richText as Array<{ text?: string }>).map(r => r.text ?? '').join('')
  if ('result' in obj) return obj.result ?? ''

  // 알 수 없는 객체 (Date 등 primitive-like 포함). Date 는 String() 으로 안전하게 문자열화됨.
  if (value instanceof Date) return value

  return ''
}

// 코드 정규화 (공백 제거, 문자열 변환)
const normalizeCode = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '')
    return null

  return String(value).trim()
}

// 파일 파싱
const parseFile = async (inputFile: File) => {
  loading.value = true
  resetMappings()
  resetSheets()

  const fileTypeConfig = getMappingFileType(mappingFileType.value)
  if (!fileTypeConfig) {
    snackbar.error('지원하지 않는 파일 형식입니다.')
    loading.value = false

    return
  }

  // .xls (Excel 97-2003 BIFF) 는 exceljs 미지원 — 친화적 안내로 선제 차단. #678
  if (/\.xls$/i.test(inputFile.name)) {
    snackbar.error('구형 .xls 형식은 더 이상 지원되지 않습니다. Excel 에서 "다른 이름으로 저장 → .xlsx" 로 변환 후 다시 업로드해주세요.')
    loading.value = false

    return
  }

  try {
    const arrayBuffer = await inputFile.arrayBuffer()
    const workbook = new ExcelJS.Workbook()

    await workbook.xlsx.load(arrayBuffer)

    workbook.worksheets.forEach((worksheet, index) => {
      // SheetJS sheet_to_json({ header: 1, defval: '', blankrows: false }) 동등 구현.
      // row.values 는 1-based sparse array 이므로 index-loop 로 hole 을 undefined 로 꺼내 '' 치환.
      const jsonData: unknown[][] = []

      worksheet.eachRow({ includeEmpty: false }, row => {
        const raw = Array.isArray(row.values) ? row.values : []
        const len = Math.max(raw.length, worksheet.columnCount + 1)
        const cols: unknown[] = []
        for (let i = 1; i < len; i++)
          cols.push(unwrapCellValue(raw[i]))

        jsonData.push(cols)
      })

      // 설정에 정의된 행 수만큼 스킵 (헤더 등)
      const dataRows = jsonData.slice(fileTypeConfig.skipRows)

      // 검증 통계
      const stats: ValidationStats = {
        valid: 0,
        duplicate: 0,
        incomplete: 0,
        empty: 0,
      }

      // 중복 검사용 Set
      const seenKeys = new Set<string>()

      // 유효한 매핑과 불완전한 매핑 분리
      const validData: MappingRow[] = []
      const incompleteData: IncompleteRow[] = []

      dataRows.forEach((row: unknown[]) => {
        const cols = fileTypeConfig.columns
        const clientItemCode = normalizeCode(row[cols.client_item_code])
        const targetItemCode = normalizeCode(row[cols.target_item_code])

        // 빈 행 스킵
        if (!clientItemCode && !targetItemCode) {
          stats.empty++

          return
        }

        // 불완전한 매핑 (한쪽만 있음)
        if (!clientItemCode || !targetItemCode) {
          stats.incomplete++
          incompleteData.push({
            client_item_code: clientItemCode,
            target_item_code: targetItemCode,
          })
          hasEmptyCode.value = true

          return
        }

        // 중복 검사
        const key = `${clientItemCode}::${targetItemCode}`
        if (seenKeys.has(key)) {
          stats.duplicate++

          return
        }

        // 유효한 매핑
        seenKeys.add(key)
        stats.valid++
        validData.push({
          client_item_code: clientItemCode,
          target_item_code: targetItemCode,
        })
      })

      sheets.value.push({
        id: index,
        name: worksheet.name,
        stats,
        driverConfigId: null,
        stored: false,
      })

      mappings.value.push({
        id: index,
        name: worksheet.name,
        validData,
        incompleteData,
        stats,
      })
    })
  }
  catch (error) {
    handleError(error, { notificationMessage: '파일 파싱에 실패했습니다.' })
  }
  finally {
    loading.value = false
  }
}

// 매핑 저장
const storeMappings = async (sheetId: number, configId: number | null) => {
  if (!configId || !selectedClient.value?.id)
    return

  const mapping = mappings.value.find(m => m.id === sheetId)
  if (!mapping || mapping.validData.length === 0) {
    snackbar.error('등록할 유효한 매핑이 없습니다.')

    return
  }

  const preparedMappingData = mapping.validData.map(m => ({
    ...m,
    config_id: configId,
  }))

  // 기존 매핑 확인
  try {
    const { success, data } = await get<{ total: number }>(
      `/review/clients/${selectedClient.value.id}/target-item-map`,
      { driver_config_id: configId, per_page: 1 },
    )

    if (success && data && data.total > 0) {
      const driverName = clientDriverConfigs.value.find(c => c.id === configId)?.download_driver.name || ''

      confirmDialog.open({
        message: `${driverName}로 이미 등록된 매핑 정보가 ${data.total}개 있습니다.`,
        data: {
          configId,
          sheetId,
          mappingData: preparedMappingData,
        },
      })

      return
    }

    await createMappings(configId, sheetId, preparedMappingData)
  }
  catch (error) {
    handleError(error, { notificationMessage: '매핑 확인에 실패했습니다.' })
  }
}

// 기존 매핑 삭제 후 재등록
const deleteAndCreateMappings = async () => {
  const dialogData = confirmDialog.data.value

  confirmDialog.close()

  if (!selectedClient.value?.id || !dialogData)
    return

  processingSheetId.value = dialogData.sheetId
  try {
    await del(`/review/clients/${selectedClient.value.id}/target-item-map?driver_config_id=${dialogData.configId}`)
    await createMappings(dialogData.configId, dialogData.sheetId, dialogData.mappingData)
  }
  catch (error) {
    handleError(error, { notificationMessage: '기존 매핑 삭제에 실패했습니다.' })
  }
  finally {
    processingSheetId.value = null
  }
}

// 매핑 생성
const createMappings = async (
  configId: number,
  sheetId: number,
  mappingData: MappingRow[],
) => {
  confirmDialog.close()

  if (!selectedClient.value?.id)
    return

  processingSheetId.value = sheetId
  try {
    const { success } = await post(
      `/review/clients/${selectedClient.value.id}/target-item-map/bulk`,
      { mappings: mappingData },
    )

    if (success) {
      snackbar.success('매핑 등록이 완료되었습니다.')

      const sheet = sheets.value.find(s => s.id === sheetId)
      if (sheet)
        sheet.stored = true
    }
    else {
      snackbar.error('매핑 등록에 실패했습니다.')
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '매핑 등록에 실패했습니다.' })
  }
  finally {
    processingSheetId.value = null
  }
}

// 드라이버 설정 로드
const loadClientDriverConfigs = async () => {
  if (!selectedClient.value?.id) {
    clientDriverConfigs.value = []

    return
  }

  try {
    const { success, data } = await get<{ data: DriverConfig[] }>(
      `/review/clients/${selectedClient.value.id}/driver-configs`,
      { per_page: 100 },
    )

    if (success && data)
      clientDriverConfigs.value = data.data || []
  }
  catch (error) {
    handleError(error, { silent: true })
  }
}

// 초기화
const resetMappings = () => {
  mappings.value = []
}

const resetSheets = () => {
  sheets.value = []
  hasEmptyCode.value = false
  showIncompleteSheets.value.clear()
}

// 불완전 매핑 표시 토글
const toggleShowIncomplete = (sheetId: number) => {
  if (showIncompleteSheets.value.has(sheetId))
    showIncompleteSheets.value.delete(sheetId)
  else
    showIncompleteSheets.value.add(sheetId)

  // Vue 반응성 트리거
  showIncompleteSheets.value = new Set(showIncompleteSheets.value)
}

const clearFile = () => {
  file.value = null
  resetMappings()
  resetSheets()
}

// 고객사 변경 감시
watch(selectedClient, () => {
  loadClientDriverConfigs()
  clearFile()
}, { immediate: true })
</script>

<template>
  <VRow>
    <!-- 고객사 선택 -->
    <VCol
      cols="12"
      md="3"
    >
      <ClientSelector />
    </VCol>

    <!-- 파일 업로드 -->
    <VCol
      cols="12"
      md="9"
    >
      <VCard>
        <VCardTitle>매핑 입력</VCardTitle>
        <VCardText>
          <VRow>
            <VCol
              cols="12"
              md="2"
            >
              <VSelect
                v-model="mappingFileType"
                :items="fileTypeOptions"
                item-title="title"
                item-value="value"
                label="파일 타입"
                :disabled="!!file || !selectedClient"
              />
            </VCol>
            <VCol
              cols="12"
              md="5"
            >
              <VFileInput
                v-model="file"
                label="Excel 파일 선택"
                placeholder="파일을 드래그하거나 클릭하세요"
                accept=".xlsx"
                prepend-icon="tabler-file-spreadsheet"
                :disabled="!selectedClient"
                :loading="loading"
              />
            </VCol>
            <VCol
              cols="12"
              md="1"
            >
              <VBtn
                color="error"
                variant="outlined"
                :disabled="!file"
                block
                @click="clearFile"
              >
                삭제
              </VBtn>
            </VCol>
          </VRow>

          <VAlert
            v-if="hasEmptyCode"
            type="warning"
            class="mt-4"
          >
            일부 행에 빈 코드가 있습니다. 미리보기에서 확인해주세요.
          </VAlert>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 시트별 채널 설정 -->
    <VCol
      v-if="file && sheets.length > 0"
      cols="12"
    >
      <VCard>
        <VCardTitle>시트별 채널 설정</VCardTitle>
        <VCardText>
          <VTable density="compact">
            <thead>
              <tr>
                <th>시트</th>
                <th>등록 가능</th>
                <th>제외 (중복/불완전)</th>
                <th style="width: 300px;">
                  채널
                </th>
                <th style="width: 120px;">
                  매핑 등록
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="sheet in sheets"
                :key="sheet.id"
              >
                <td>{{ sheet.name }}</td>
                <td>
                  <VChip
                    color="success"
                    size="small"
                    variant="tonal"
                  >
                    {{ sheet.stats.valid }}건
                  </VChip>
                </td>
                <td>
                  <VChip
                    v-if="sheet.stats.duplicate > 0"
                    color="warning"
                    size="small"
                    variant="tonal"
                    class="me-1"
                  >
                    중복 {{ sheet.stats.duplicate }}
                  </VChip>
                  <VChip
                    v-if="sheet.stats.incomplete > 0"
                    color="error"
                    size="small"
                    variant="tonal"
                  >
                    불완전 {{ sheet.stats.incomplete }}
                  </VChip>
                  <span
                    v-if="sheet.stats.duplicate === 0 && sheet.stats.incomplete === 0"
                    class="text-grey"
                  >
                    -
                  </span>
                </td>
                <td>
                  <VSelect
                    v-model="sheet.driverConfigId"
                    :items="clientDriverConfigs"
                    :item-title="(item: DriverConfig) => item.download_driver.name"
                    item-value="id"
                    density="compact"
                    hide-details
                    :disabled="sheet.stored"
                    placeholder="채널 선택"
                  />
                </td>
                <td>
                  <VBtn
                    :color="sheet.stored ? 'success' : 'primary'"
                    variant="outlined"
                    size="small"
                    :disabled="sheet.stored || !sheet.driverConfigId || sheet.stats.valid === 0"
                    :loading="processingSheetId === sheet.id"
                    @click="storeMappings(sheet.id, sheet.driverConfigId)"
                  >
                    {{ sheet.stored ? '완료' : '등록' }}
                  </VBtn>
                </td>
              </tr>
            </tbody>
          </VTable>
        </VCardText>
      </VCard>
    </VCol>

    <!-- Excel 미리보기 -->
    <VCol
      v-if="file && mappings.length > 0"
      cols="12"
    >
      <VCard>
        <VCardTitle>Excel 미리보기</VCardTitle>
        <VCardText>
          <VTabs v-model="previewTab">
            <VTab
              v-for="mapping in mappings"
              :key="mapping.id"
              :value="mapping.id"
            >
              {{ mapping.name }} ({{ mapping.stats.valid }}건)
            </VTab>
          </VTabs>

          <VWindow v-model="previewTab">
            <VWindowItem
              v-for="mapping in mappings"
              :key="mapping.id"
              :value="mapping.id"
            >
              <!-- 불완전한 매핑 경고 -->
              <VAlert
                v-if="mapping.incompleteData.length > 0"
                type="warning"
                variant="tonal"
                class="mt-4 mb-2"
              >
                <div class="d-flex align-center justify-space-between">
                  <span>불완전한 매핑 {{ mapping.incompleteData.length }}건이 제외됩니다.</span>
                  <VBtn
                    size="small"
                    variant="text"
                    @click="toggleShowIncomplete(mapping.id)"
                  >
                    {{ showIncompleteSheets.has(mapping.id) ? '숨기기' : '보기' }}
                  </VBtn>
                </div>
                <VTable
                  v-if="showIncompleteSheets.has(mapping.id)"
                  density="compact"
                  class="mt-2 bg-transparent"
                >
                  <tbody>
                    <tr
                      v-for="(item, index) in mapping.incompleteData.slice(0, 10)"
                      :key="index"
                    >
                      <td style="width: 50%;">
                        {{ item.target_item_code || '(빈 값)' }}
                      </td>
                      <td>{{ item.client_item_code || '(빈 값)' }}</td>
                    </tr>
                  </tbody>
                </VTable>
                <div
                  v-if="showIncompleteSheets.has(mapping.id) && mapping.incompleteData.length > 10"
                  class="text-caption mt-1"
                >
                  ... 외 {{ mapping.incompleteData.length - 10 }}건
                </div>
              </VAlert>

              <!-- 중복 매핑 알림 -->
              <VAlert
                v-if="mapping.stats.duplicate > 0"
                type="info"
                variant="tonal"
                class="mt-4 mb-2"
              >
                중복 매핑 {{ mapping.stats.duplicate }}건이 제외됩니다.
              </VAlert>

              <!-- 유효한 매핑 테이블 -->
              <VTable
                density="compact"
                class="mt-4"
              >
                <thead>
                  <tr>
                    <th style="width: 60px;">
                      #
                    </th>
                    <th>외부채널 상품 코드</th>
                    <th>자사몰 상품 코드</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, index) in mapping.validData.slice(0, 100)"
                    :key="index"
                  >
                    <td>{{ index + 1 }}</td>
                    <td>{{ item.target_item_code }}</td>
                    <td>{{ item.client_item_code }}</td>
                  </tr>
                </tbody>
              </VTable>
              <div
                v-if="mapping.validData.length > 100"
                class="text-center pa-4 text-muted"
              >
                ... 외 {{ mapping.validData.length - 100 }}개 항목
              </div>
              <div
                v-if="mapping.validData.length === 0"
                class="text-center pa-4 text-grey"
              >
                등록 가능한 유효한 매핑이 없습니다.
              </div>
            </VWindowItem>
          </VWindow>
        </VCardText>
      </VCard>
    </VCol>

    <!-- 확인 다이얼로그 (매핑 등록) -->
    <VDialog
      :model-value="confirmDialog.isOpen.value"
      max-width="500"
      @update:model-value="!$event && confirmDialog.close()"
    >
      <VCard>
        <VCardTitle>매핑 등록 확인</VCardTitle>
        <VCardText>
          {{ confirmDialog.message.value }}
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="error"
            variant="outlined"
            @click="deleteAndCreateMappings"
          >
            기존 매핑 삭제 후 재등록
          </VBtn>
          <VBtn
            color="primary"
            @click="confirmDialog.data.value && createMappings(confirmDialog.data.value.configId, confirmDialog.data.value.sheetId, confirmDialog.data.value.mappingData)"
          >
            기존 매핑 유지 및 추가 등록
          </VBtn>
          <VBtn
            variant="outlined"
            @click="confirmDialog.close()"
          >
            취소
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar.isVisible.value"
      :color="snackbar.color.value"
      :timeout="snackbar.timeout.value"
    >
      {{ snackbar.message.value }}
    </VSnackbar>
  </VRow>
</template>
